// src/components/SellCTASection.jsx
import React from 'react';

export default function SellCTASection() {
  return (
    <>
      <style>{`
        /* ===== MRM Sell CTA (namespaced) ===== */
        .mrm-sellcta{
          /* Scoped design tokens */
          --mrm-sellcta-brand:#1e3a8a;
          --mrm-sellcta-brand2:#536da8;
          --mrm-sellcta-ink:#ffffff;
          --mrm-sellcta-muted:#e2e8f0;
          --mrm-sellcta-btn:#1e3a8a;
          --mrm-sellcta-btn-hover:#173074;
          --mrm-sellcta-line:rgba(255,255,255,.22);
          --mrm-sellcta-bg:url('/media/hero-property-poster.jpg');

          position:relative; isolation:isolate;
          background-image: var(--mrm-sellcta-bg);
          background-size: cover; background-position: center;
          color: var(--mrm-sellcta-ink); text-align:center;
          padding: clamp(3rem, 8vw, 7rem) 1rem;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        }
        .mrm-sellcta::before{
          content:""; position:absolute; inset:0; z-index:0;
          background:
            linear-gradient(180deg, rgba(2,6,23,.15) 0%, rgba(2,6,23,.55) 100%),
            radial-gradient(1200px 600px at 15% -10%, rgba(14,165,233,.25), transparent 60%);
        }
        .mrm-sellcta__grain{
          position:absolute; inset:0; pointer-events:none; z-index:0; opacity:.15;
          background-image:url('data:image/svg+xml;utf8,${encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="140" height="140" viewBox="0 0 140 140"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(#n)" opacity="0.25"/></svg>`
          )}');
          mix-blend-mode:soft-light;
        }
        .mrm-sellcta__wrap{ position:relative; z-index:1; max-width: 1000px; margin:0 auto; }

        .mrm-sellcta__eyebrow{
          display:inline-flex; align-items:center; gap:.5rem;
          padding:.4rem .8rem; border-radius:999px;
          background: rgba(255,255,255,.1);
          border:1px solid var(--mrm-sellcta-line);
          font-weight:800; letter-spacing:.12em; text-transform:uppercase; font-size:.78rem;
        }
        .mrm-sellcta__title{
          margin:.75rem auto 1rem; max-width: 22ch;
          font-size: clamp(1.6rem, 4vw, 2.6rem); line-height:1.08; font-weight:900;
          text-shadow: 0 2px 18px rgba(0,0,0,.25);
        }
        .mrm-sellcta__underline{
          width: 110px; height: 4px; margin: .6rem auto 1rem;
          background: linear-gradient(90deg, var(--mrm-sellcta-brand), var(--mrm-sellcta-brand2));
          border-radius: 999px;
        }
        .mrm-sellcta__subtitle{
          margin: 0 auto 1.25rem; max-width: 60ch; color: var(--mrm-sellcta-muted); font-size:1.05rem;
        }

        .mrm-sellcta__chips{ display:flex; gap:.6rem; flex-wrap:wrap; justify-content:center; margin: .5rem 0 1.5rem; }
        .mrm-sellcta__chip{
          display:inline-flex; align-items:center; gap:.5rem; color:#fff;
          background: rgba(255,255,255,.12); border:1px solid var(--mrm-sellcta-line);
          padding:.45rem .7rem; border-radius:10px; font-weight:700; font-size:.84rem;
        }
        .mrm-sellcta__chip-dot{ width:8px; height:8px; border-radius:50%; background:var(--mrm-sellcta-brand); box-shadow:0 0 0 3px rgba(30,58,138,.25); }

        .mrm-sellcta__btns{
          display:flex; flex-direction:column; gap:.85rem; align-items:center; justify-content:center;
        }
        @media (min-width: 520px){ .mrm-sellcta__btns{ flex-direction:row; } }

        .mrm-sellcta__btn{
          appearance:none; border:none; cursor:pointer; text-decoration:none; color:#fff;
          font-weight:800; border-radius:999px; padding:.9rem 1.2rem; min-width: 210px;
          transition: transform .15s ease, box-shadow .25s ease, background .2s ease, color .2s ease;
          display:inline-flex; align-items:center; justify-content:center; gap:.5rem;
        }
        .mrm-sellcta__btn--primary{ background: var(--mrm-sellcta-btn); box-shadow: 0 10px 26px rgba(30,58,138,.35); }
        .mrm-sellcta__btn--primary:hover{ background: var(--mrm-sellcta-btn-hover); transform: translateY(-1px); }
        .mrm-sellcta__btn--ghost{
          background: rgba(255,255,255,.08); color:#fff; border:1px solid var(--mrm-sellcta-line);
        }
        .mrm-sellcta__btn--ghost:hover{ transform: translateY(-1px); box-shadow: 0 8px 18px rgba(255,255,255,.15); }

        .mrm-sellcta__meta{
          display:flex; gap:.7rem; justify-content:center; flex-wrap:wrap;
          margin-top:1rem; color: var(--mrm-sellcta-muted); font-size:.9rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .mrm-sellcta__btn, .mrm-sellcta__btn--primary, .mrm-sellcta__btn--ghost { transition: none; }
        }
      `}</style>

      <section
        className="mrm-sellcta"
        aria-label="Sell your property with MRM Lettings Property & Management Ltd"
        /* To override background per-page, pass a CSS var:
           style={{ '--mrm-sellcta-bg': "url('https://your-image.jpg')" }} */
      >
        <div className="mrm-sellcta__grain" aria-hidden="true" />
        <div className="mrm-sellcta__wrap">
          <span className="mrm-sellcta__eyebrow">Thinking of selling?</span>
          <h2 className="mrm-sellcta__title">Get a valuation and sell with confidence</h2>
          <div className="mrm-sellcta__underline" aria-hidden="true"></div>
          <p className="mrm-sellcta__subtitle">
            One dedicated Middlesbrough office with local experts and deep area knowledge. Professional
            marketing, qualified buyers and clear updates from valuation to completion.
          </p>

          <div className="mrm-sellcta__chips" role="list">
            <span className="mrm-sellcta__chip" role="listitem"><span className="mrm-sellcta__chip-dot" aria-hidden="true"></span> Local market appraisal</span>
            <span className="mrm-sellcta__chip" role="listitem"><span className="mrm-sellcta__chip-dot" aria-hidden="true"></span> Professional photos &amp; floorplans</span>
            <span className="mrm-sellcta__chip" role="listitem"><span className="mrm-sellcta__chip-dot" aria-hidden="true"></span> Clear progress updates</span>
          </div>

          <div className="mrm-sellcta__btns">
            <a href="/sellers/valuation" className="mrm-sellcta__btn mrm-sellcta__btn--primary" aria-label="Start your online valuation">
              Start your valuation
            </a>
            <a href="/contact" className="mrm-sellcta__btn mrm-sellcta__btn--ghost" aria-label="Book a free market appraisal">
              Book a free appraisal
            </a>
            <a href="/properties" className="mrm-sellcta__btn mrm-sellcta__btn--ghost" aria-label="Browse current properties">
              Browse properties
            </a>
          </div>

          <div className="mrm-sellcta__meta">
            <span>Simple online valuation request</span>
            <span>•</span>
            <span>In-person valuation available</span>
          </div>
        </div>
      </section>
    </>
  );
}
